import { Permission } from '@bookora/shared';

import type { StepContext, TourDef } from './types';

/**
 * On a phone the persona's focus step has no anchor (the sidebar items live
 * behind the menu button), so the menu step carries its one sentence instead:
 * the same choice, in the same order, as the `focus` group below.
 */
function compactFocus(ctx: StepContext): string {
  if (
    ctx.persona === 'owner' &&
    ctx.onboardingIncomplete === true &&
    ctx.can(Permission.SETTINGS_EDIT)
  ) {
    return 'مرحله‌های راه‌اندازی کلینیک در بخش «تنظیمات» است.';
  }
  if (ctx.can(Permission.APPOINTMENT_VIEW)) {
    return 'نوبت‌های امروز در بخش «تقویم نوبت‌ها» است.';
  }
  if (ctx.persona === 'finance') {
    return 'دریافت‌ها، هزینه‌ها و سود و زیان در بخش «حسابداری» است.';
  }
  return 'فقط بخش‌هایی رو می‌بینید که نقش شما به اون‌ها دسترسی داره.';
}

/**
 * «آشنایی با پنل» — shell anchors only, so it runs on any route. Three to
 * five steps: the nav, one persona focus (wide only), search, the bell, and
 * this very Help entry last. Its finish is resolved per viewer in
 * `registry.ts` (it needs the other tours): «ادامهٔ راه‌اندازی» for an owner
 * with setup open, otherwise «بعدی: …» to the first recommended tour.
 */
export const CORE_TOUR: TourDef = {
  key: 'core',
  version: 1,
  surface: 'shell',
  title: 'آشنایی با پنل',
  hubDescription: 'منوی بخش‌ها، جست‌وجوی بیمار، اعلان‌ها و همین راهنما.',
  autoOffer: false,
  maxSteps: 5,
  minSteps: 2,
  steps: [
    {
      id: 'nav',
      anchor: { wide: 'shell.nav', compact: 'shell.menu-button' },
      placement: 'end',
      title: (ctx) => (ctx.viewport === 'compact' ? 'منوی بخش‌ها' : 'همهٔ بخش‌ها اینجاست'),
      body: (ctx) =>
        ctx.viewport === 'compact'
          ? `با زدن این دکمه فهرست بخش‌ها باز می‌شه. ${compactFocus(ctx)}`
          : 'بخش‌هایی که به اون‌ها دسترسی دارید در این منو نمایش داده می‌شن. بخش بازشده پررنگ‌تره.',
    },
    {
      id: 'focus-setup',
      group: 'focus',
      anchor: { wide: 'nav.settings', compact: null },
      placement: 'end',
      personas: ['owner'],
      requires: { all: [Permission.SETTINGS_EDIT] },
      when: (ctx) => ctx.onboardingIncomplete === true,
      title: 'راه‌اندازی کلینیک',
      body: 'مشخصات کلینیک، ساعت‌های کاری، اشتراک و اعتبار پیامک در «تنظیمات» است. فهرست مراحل راه‌اندازی هم همون‌جاست.',
    },
    {
      id: 'focus-calendar-doctor',
      group: 'focus',
      anchor: { wide: 'nav.calendar', compact: null },
      placement: 'end',
      personas: ['doctor'],
      requires: { all: [Permission.APPOINTMENT_VIEW] },
      title: 'نوبت‌های شما',
      body: 'در «تقویم نوبت‌ها» نوبت‌های امروز و این هفته رو می‌بینید. با کلیک روی هر نوبت، جزئیاتش باز می‌شه.',
    },
    {
      id: 'focus-calendar',
      group: 'focus',
      anchor: { wide: 'nav.calendar', compact: null },
      placement: 'end',
      requires: { all: [Permission.APPOINTMENT_VIEW] },
      title: 'تقویم نوبت‌ها',
      body: (ctx) =>
        ctx.can(Permission.APPOINTMENT_CONFIRM_REQUESTS)
          ? 'ثبت نوبت جدید، جابه‌جایی نوبت‌ها و تأیید درخواست‌های آنلاین همه در این بخش است.'
          : 'نوبت‌های کلینیک رو اینجا می‌بینید. اگر دسترسی ثبت نوبت داشته باشید، می‌تونید نوبت جدید هم ثبت کنید.',
    },
    {
      id: 'focus-accounting',
      group: 'focus',
      anchor: { wide: 'nav.accounting', compact: null },
      placement: 'end',
      personas: ['finance'],
      title: 'حسابداری',
      body: 'دریافت‌ها، هزینه‌ها و سود و زیان کلینیک رو در این بخش، برای هر بازه‌ای که بخواید، می‌بینید.',
    },
    {
      id: 'search',
      anchor: 'shell.search',
      placement: 'bottom',
      requires: { all: [Permission.PATIENT_VIEW] },
      title: 'پیدا کردن بیمار',
      body: (ctx) =>
        ctx.viewport === 'compact'
          ? 'این دکمه رو بزنید و نام، موبایل یا شمارهٔ پرونده رو جست‌وجو کنید.'
          : 'نام، موبایل یا شمارهٔ پرونده رو بنویسید تا بیمار رو پیدا کنید. از هر صفحه‌ای با زدن کلید «/» هم به این کادر می‌رسید.',
    },
    {
      id: 'bell',
      anchor: 'shell.bell',
      placement: 'bottom-end',
      title: 'اعلان‌ها',
      body: 'اعلان‌های مربوط به کار شما، مثل لغو نوبت یا درخواست جدید، اینجا نمایش داده می‌شن. عدد روی زنگ، تعداد اعلان‌های خوانده‌نشده است.',
    },
    {
      id: 'help',
      anchor: { wide: 'shell.help', compact: 'shell.user-menu' },
      placement: 'bottom-end',
      title: 'هر وقت سؤالی داشتید',
      body: (ctx) =>
        ctx.viewport === 'compact'
          ? 'راهنمای هر بخش، راهنمای آشنایی با پنل و پیشنهادهای بوکورا در منوی حساب شما، بخش «راهنما»، است.'
          : 'راهنمای هر بخش، راهنمای آشنایی با پنل و پیشنهادهای بوکورا در این دکمه است.',
    },
  ],
};
